import { createPortal } from 'react-dom';
import { LoaderCircle } from 'lucide-react';
import { cn } from '@/shared/lib/utils';

const SIZE_CLASS = {
  xs: 'size-3.5',
  sm: 'size-4',
  md: 'size-8',
  lg: 'size-10',
};

const TONE_CLASS = {
  brand: 'text-brand',
  current: 'text-current',
};

type SpinnerProps = {
  size?: keyof typeof SIZE_CLASS;
  tone?: keyof typeof TONE_CLASS;
  label?: string | null;
};

/**
 * L'indicatore di attesa dell'app. Annuncia `label` come stato; con `label={null}`
 * è solo decorativo, per quando accanto c'è già un testo visibile.
 */
export function Spinner({ size = 'sm', tone = 'brand', label = 'Caricamento…' }: SpinnerProps) {
  const icon = (
    <LoaderCircle
      aria-hidden
      className={cn('shrink-0 animate-spin', SIZE_CLASS[size], TONE_CLASS[tone])}
    />
  );
  if (label === null) return icon;
  return (
    <span role="status" className="inline-flex">
      {icon}
      <span className="sr-only">{label}</span>
    </span>
  );
}

/** L'attesa che blocca la pagina durante un'operazione: velo e riquadro con il testo, nel portale. */
export function BusyOverlay({ label }: { label: string }) {
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-backdrop/50">
      <div
        role="status"
        className="surface-overlay flex items-center gap-3 rounded-xl p-6 text-sm text-default"
      >
        <Spinner label={null} />
        {label}
      </div>
    </div>,
    document.body
  );
}
